import React, { useState, useEffect } from 'react';
import { api, API } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Download, FileText, Users, Calendar, Filter, Info, Columns, ChevronDown, ChevronUp, BarChart3, ShieldCheck } from 'lucide-react';

// Colunas disponíveis para o relatório de consultas
const COLUNAS_DISPONIVEIS = [
  { id: 'seq', label: 'SEQ', descricao: 'Número sequencial', default: true },
  { id: 'paciente_nome', label: 'NOME DO PACIENTE', descricao: 'Nome completo', default: true },
  { id: 'paciente_cpf', label: 'CPF', descricao: 'CPF do paciente', default: true },
  { id: 'data_consulta', label: 'DATA', descricao: 'Data agendada', default: true },
  { id: 'horario', label: 'HORA', descricao: 'Horário agendado', default: true },
  { id: 'paciente_telefone', label: 'FONE', descricao: 'Contato telefônico', default: true },
  { id: 'num_reg', label: 'Nº REG.', descricao: 'Número de registro da marcação', default: true },
  { id: 'procedimento', label: 'PROCEDIMENTO', descricao: 'Nome do procedimento', default: true },
  { id: 'paciente_sus', label: 'Nº CARTÃO DO SUS', descricao: 'Número do cartão SUS', default: true },
  { id: 'paciente_endereco', label: 'ENDEREÇO', descricao: 'Endereço completo', default: true },
  { id: 'psf', label: 'PSF', descricao: 'Unidade de Saúde (PSF)', default: true },
  { id: 'marcador', label: 'MARCADOR', descricao: 'Quem realizou a marcação', default: true },
  { id: 'id', label: 'ID da Consulta', descricao: 'Identificador único', default: false },
  { id: 'paciente_data_nascimento', label: 'Data de Nascimento', descricao: 'Data de nascimento', default: false },
  { id: 'tipo', label: 'Tipo', descricao: 'Consulta ou Exame', default: false },
  { id: 'sigla', label: 'Sigla', descricao: 'Sigla do procedimento (LAB, RX, USG...)', default: false },
  { id: 'codigo_procedimento', label: 'Código do Procedimento', descricao: 'Código SIGTAP', default: false },
  { id: 'medico', label: 'Médico/Profissional', descricao: 'Profissional responsável', default: false },
  { id: 'status', label: 'Status', descricao: 'Marcada, Realizada ou Cancelada', default: false },
  { id: 'data_solicitacao', label: 'Data da Solicitação', descricao: 'Quando foi solicitada', default: false },
  { id: 'observacoes', label: 'Observações', descricao: 'Notas adicionais', default: false },
];

export default function ReportsPage() {
  const { isAdmin, user, isRelator, getRelatorOrigin, isMaster } = useAuth();
  const [units, setUnits] = useState([]);
  const [siglas, setSiglas] = useState({});
  
  // Admin só pode ver dados da sua unidade (exceto Admin da Secretaria)
  const ROLE_LEVELS = { master: 5, admin_geral: 4, admin: 3, marcador_geral: 2, marcador: 1, relator_hospital: 1, relator_lab_municipal: 1 };
  const roleLevel = ROLE_LEVELS[user?.role] || 0;
  const isSecretaria = user?.is_secretaria === true;
  const isAdminOnly = user?.role === 'admin' && !isSecretaria;
  const relatorOrigin = getRelatorOrigin ? getRelatorOrigin() : null;
  const isRelatorUser = isRelator ? isRelator() : false;
  const canChangeUnitFilter = !isRelatorUser && (roleLevel >= ROLE_LEVELS.marcador_geral || isSecretaria);
  const isAdminGeralPlus = !isRelatorUser && (roleLevel >= ROLE_LEVELS.admin_geral || isSecretaria);
  
  const [filters, setFilters] = useState({
    unit_id: isAdminOnly && user?.unit_id ? user.unit_id : '',
    start_date: '',
    end_date: '',
    requested_start_date: '',
    requested_end_date: '',
    status: 'Marcada',
    appointment_type: '',
    sigla: '',
    turno: '',
    // Relator: origem travada pela designacao
    origin: relatorOrigin || ''
  });
  
  // Estado para colunas selecionadas
  const [colunasExpanded, setColunasExpanded] = useState(false);
  const [colunasSelecionadas, setColunasSelecionadas] = useState(() => {
    const defaults = COLUNAS_DISPONIVEIS.filter(c => c.default).map(c => c.id);
    // Por padrao, Lab. Municipal nao inclui "Numero de Registro" nos relatorios
    if (relatorOrigin === 'lab_municipal') {
      return defaults.filter(id => id !== 'num_reg');
    }
    return defaults;
  });

  // Ajuste automatico: quando o filtro de origem muda para Lab. Municipal,
  // desmarca "Num. REG."; quando volta para Hospital/Todos, pode ser marcado manualmente.
  useEffect(() => {
    if (filters.origin === 'lab_municipal') {
      setColunasSelecionadas(prev => prev.filter(id => id !== 'num_reg'));
    }
  }, [filters.origin]);

  useEffect(() => {
    if (isAdmin() || isRelatorUser) {
      if (!isRelatorUser) fetchUnits();
      fetchSiglas();
    }
  }, []);

  const fetchUnits = async () => {
    try {
      const response = await api.get('/health-units');
      setUnits(response.data);
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
    }
  };

  const fetchSiglas = async () => {
    try {
      const response = await api.get('/siglas');
      setSiglas(response.data);
    } catch (error) {
      console.error('Erro ao carregar siglas:', error);
    }
  };

  const toggleColuna = (colunaId) => {
    setColunasSelecionadas(prev => {
      if (prev.includes(colunaId)) {
        return prev.filter(id => id !== colunaId);
      } else {
        return [...prev, colunaId];
      }
    });
  };

  const selecionarTodas = () => {
    setColunasSelecionadas(COLUNAS_DISPONIVEIS.map(c => c.id));
  };

  const limparSelecao = () => {
    setColunasSelecionadas(['id', 'paciente_nome']);  // Manter pelo menos essas
  };

  const handleExportAppointments = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.unit_id) params.append('unit_id', filters.unit_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.requested_start_date) params.append('requested_start_date', filters.requested_start_date);
      if (filters.requested_end_date) params.append('requested_end_date', filters.requested_end_date);
      if (filters.status) params.append('status', filters.status);
      if (filters.appointment_type) params.append('appointment_type', filters.appointment_type);
      if (filters.sigla) params.append('sigla', filters.sigla);
      if (filters.turno) params.append('turno', filters.turno);
      if (filters.origin) params.append('origin', filters.origin);
      // Enviar colunas na ordem canonica (COLUNAS_DISPONIVEIS) para manter
      // CPF logo apos o nome, independente da ordem de selecao do usuario.
      COLUNAS_DISPONIVEIS
        .filter(c => colunasSelecionadas.includes(c.id))
        .forEach(c => params.append('colunas', c.id));

      // Usa fetch direto (nao axios+blob) para evitar bug "responseText on blob" no XHR
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/export/appointments?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        // Servidor retornou erro - le como JSON/texto para pegar o detail
        const text = await res.text();
        let detail = 'Erro ao exportar relatorio de consultas';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = String(parsed.detail);
        } catch {
          if (text) detail = text.slice(0, 300);
        }
        toast.error(detail);
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `consultas_exames_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Relatório de consultas exportado com sucesso!');
    } catch (error) {
      toast.error('Erro de rede ao exportar relatorio. Tente novamente.');
    }
  };

  /**
   * Faz download de relatorio XLSX via fetch (evita bug axios+blob de responseText).
   * Em caso de erro HTTP, le o JSON e mostra toast com o detail do backend.
   */
  const downloadReport = async (path, filename, errorFallback) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}${path}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      let detail = errorFallback;
      try {
        const parsed = JSON.parse(text);
        if (parsed?.detail) detail = String(parsed.detail);
      } catch {
        if (text) detail = text.slice(0, 300);
      }
      toast.error(detail);
      return false;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  };

  const handleExportPatients = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const ok = await downloadReport('/export/patients', `pacientes_${today}.xlsx`, 'Erro ao exportar relatorio de pacientes');
      if (ok) toast.success('Relatório de pacientes exportado com sucesso!');
    } catch {
      toast.error('Erro de rede ao exportar relatorio de pacientes.');
    }
  };

  const handleExportTermsAcceptances = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const ok = await downloadReport('/export/terms-acceptances', `aceites_termos_${today}.xlsx`, 'Erro ao exportar documento de aceites');
      if (ok) toast.success('Documento de aceites exportado com sucesso!');
    } catch {
      toast.error('Erro de rede ao exportar documento de aceites.');
    }
  };

  const handleExportBalancoGeral = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.origin) params.append('origin', filters.origin);
      const today = new Date().toISOString().split('T')[0];
      const ok = await downloadReport(`/export/balanco-geral?${params.toString()}`, `balanco_geral_${today}.xlsx`, 'Erro ao exportar Balanco Geral');
      if (ok) toast.success('Balanço Geral exportado com sucesso!');
    } catch {
      toast.error('Erro de rede ao exportar Balanco Geral.');
    }
  };

  const handleExportBalancoOperacional = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.origin) params.append('origin', filters.origin);
      const today = new Date().toISOString().split('T')[0];
      const ok = await downloadReport(`/export/balanco-operacional?${params.toString()}`, `balanco_operacional_${today}.xlsx`, 'Erro ao exportar Balanco Operacional');
      if (ok) toast.success('Balanço Operacional exportado com sucesso!');
    } catch {
      toast.error('Erro de rede ao exportar Balanco Operacional.');
    }
  };

  const clearFilters = () => {
    setFilters({
      unit_id: '',
      start_date: '',
      end_date: '',
      requested_start_date: '',
      requested_end_date: '',
      status: 'Marcada',
      appointment_type: '',
      sigla: '',
      turno: '',
      // Relator: origem sempre travada
      origin: relatorOrigin || ''
    });
  };

  if (!isAdmin() && !isRelatorUser) {
    return (
      <div className="text-center py-12">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 max-w-md mx-auto">
          <Info className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-amber-800 mb-2">Acesso Restrito</h2>
          <p className="text-amber-600">
            Esta funcionalidade está disponível apenas para administradores.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Relatórios</h1>
        <p className="text-slate-600">Exporte dados do sistema em formato Excel</p>
      </div>

      {isRelatorUser && (
        <div
          className={`mb-6 p-4 rounded-xl border ${
            relatorOrigin === 'hospital'
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-200'
          }`}
          data-testid="relator-deadline-banner"
        >
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${relatorOrigin === 'hospital' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
              <FileText className={`h-5 w-5 ${relatorOrigin === 'hospital' ? 'text-emerald-700' : 'text-amber-700'}`} />
            </div>
            <div className="flex-1">
              <h3 className={`font-bold ${relatorOrigin === 'hospital' ? 'text-emerald-900' : 'text-amber-900'}`}>
                Relator designado - {relatorOrigin === 'hospital' ? 'Hospital' : 'Lab. Municipal'}
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Relatório de Consultas e Exames */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-teal-50">
              <FileText className="h-6 w-6 text-teal-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Relatório de Consultas e Exames</h2>
              <p className="text-sm text-slate-600">Exporte as marcações com filtros personalizados</p>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-slate-50 p-4 rounded-lg mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-5 w-5 text-slate-600" />
              <h3 className="font-semibold text-slate-900">Filtros</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {!isRelatorUser && (
                <div>
                  <Label className="text-xs">Local de Atendimento</Label>
                  <Select 
                    value={filters.origin || 'all'} 
                    onValueChange={(value) => setFilters({ ...filters, origin: value === 'all' ? '' : value })}
                  >
                    <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="filter-origin-select">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="hospital">Hospital</SelectItem>
                      <SelectItem value="lab_municipal">Lab. Municipal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!isRelatorUser && (
                <div>
                  <Label htmlFor="unit" className="text-xs">Unidade de Saude</Label>
                  <Select 
                    value={filters.unit_id} 
                    onValueChange={(value) => setFilters({ ...filters, unit_id: value === 'all' ? '' : value })}
                    disabled={isAdminOnly}
                  >
                    <SelectTrigger className="h-10 rounded-lg mt-1">
                      <SelectValue placeholder="Todas as unidades" />
                    </SelectTrigger>
                    <SelectContent>
                      {canChangeUnitFilter && <SelectItem value="all">Todas as unidades</SelectItem>}
                      {units.map((unit) => (
                        <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isAdminOnly && (
                    <p className="text-xs text-slate-500 mt-1">Relatórios restritos à sua unidade.</p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="type" className="text-xs">Tipo</Label>
                <Select 
                  value={filters.appointment_type} 
                  onValueChange={(value) => setFilters({ ...filters, appointment_type: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="h-10 rounded-lg mt-1">
                    <SelectValue placeholder="Todos os tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="Consulta">Consulta</SelectItem>
                    <SelectItem value="Exame">Exame</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start_date" className="text-xs">Data Atendimento (Início)</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="h-10 rounded-lg mt-1"
                />
              </div>

              <div>
                <Label htmlFor="end_date" className="text-xs">Data Atendimento (Fim)</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="h-10 rounded-lg mt-1"
                />
              </div>

              <div>
                <Label htmlFor="requested_start_date" className="text-xs">Data Solicitação (Início)</Label>
                <Input
                  id="requested_start_date"
                  type="date"
                  value={filters.requested_start_date}
                  onChange={(e) => setFilters({ ...filters, requested_start_date: e.target.value })}
                  className="h-10 rounded-lg mt-1"
                />
              </div>

              <div>
                <Label htmlFor="requested_end_date" className="text-xs">Data Solicitação (Fim)</Label>
                <Input
                  id="requested_end_date"
                  type="date"
                  value={filters.requested_end_date}
                  onChange={(e) => setFilters({ ...filters, requested_end_date: e.target.value })}
                  className="h-10 rounded-lg mt-1"
                />
              </div>

              <div>
                <Label htmlFor="sigla_filter" className="text-xs">Procedimento (Sigla)</Label>
                <Select 
                  value={filters.sigla} 
                  onValueChange={(value) => setFilters({ ...filters, sigla: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="h-10 rounded-lg mt-1">
                    <SelectValue placeholder="Todos os procedimentos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os procedimentos</SelectItem>
                    {Object.entries(siglas).map(([sigla, info]) => (
                      <SelectItem key={sigla} value={sigla}>{sigla} - {info.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="status_filter" className="text-xs">Status</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="h-10 rounded-lg mt-1">
                    <SelectValue placeholder="Todos os status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="Marcada">Marcada</SelectItem>
                    <SelectItem value="Realizada">Realizada</SelectItem>
                    <SelectItem value="Cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="turno_filter" className="text-xs">Turno</Label>
                <Select 
                  value={filters.turno} 
                  onValueChange={(value) => setFilters({ ...filters, turno: value === 'all' ? '' : value })}
                >
                  <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="turno-filter">
                    <SelectValue placeholder="Todos os turnos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os turnos</SelectItem>
                    <SelectItem value="manha">Manha (07:00 - 12:00)</SelectItem>
                    <SelectItem value="tarde">Tarde (12:00 - 18:00)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Seleção de Colunas */}
          <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-200">
            <button
              type="button"
              onClick={() => setColunasExpanded(!colunasExpanded)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Columns className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold text-indigo-900">Colunas do Relatório</h3>
                <span className="text-sm text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                  {colunasSelecionadas.length} de {COLUNAS_DISPONIVEIS.length} selecionadas
                </span>
              </div>
              {colunasExpanded ? (
                <ChevronUp className="h-5 w-5 text-indigo-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-indigo-600" />
              )}
            </button>

            {colunasExpanded && (
              <div className="mt-4">
                <div className="flex gap-3 mb-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={selecionarTodas}
                    className="text-xs"
                  >
                    Selecionar Todas
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={limparSelecao}
                    className="text-xs"
                  >
                    Limpar Seleção
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {COLUNAS_DISPONIVEIS.map((coluna) => (
                    <label
                      key={coluna.id}
                      className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        colunasSelecionadas.includes(coluna.id)
                          ? 'bg-white border-indigo-400 shadow-sm'
                          : 'bg-indigo-50/50 border-indigo-200 opacity-70'
                      }`}
                    >
                      <Checkbox
                        checked={colunasSelecionadas.includes(coluna.id)}
                        onCheckedChange={() => toggleColuna(coluna.id)}
                        className="mt-0.5"
                      />
                      <div>
                        <p className="text-sm font-medium text-indigo-900">{coluna.label}</p>
                        <p className="text-xs text-indigo-600">{coluna.descricao}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={handleExportAppointments}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="export-appointments-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar Consultas/Exames
            </Button>
            <Button 
              variant="outline" 
              onClick={clearFilters}
            >
              Limpar Filtros
            </Button>
          </div>
        </div>

        {/* Relatório de Pacientes */}
        {!isRelatorUser && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-emerald-50">
              <Users className="h-6 w-6 text-emerald-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Relatório de Pacientes</h2>
              <p className="text-sm text-slate-600">Exporte a lista completa de pacientes</p>
            </div>
          </div>

          <div className="bg-emerald-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-emerald-700">
              Este relatório inclui todos os pacientes cadastrados no sistema com seus dados completos:
              nome, CPF, cartão SUS, data de nascimento, endereço e telefone.
            </p>
          </div>

          <Button 
            onClick={handleExportPatients}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="export-patients-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Pacientes
          </Button>
        </div>
        )}

        {/* Balanço Operacional - Admin Geral+ */}
        {isAdminGeralPlus && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="h-6 w-6 text-teal-600" />
            <h3 className="text-lg font-semibold text-slate-900">Balanço Geral Operacional</h3>
          </div>
          <div className="bg-teal-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-teal-700">
              Relatório técnico-estratégico com análise comparativa completa entre todas as unidades. 
              Inclui: visão geral, volume de agendamentos, taxa de ocupação, cancelamentos, 
              especialidades mais demandadas, ranking de desempenho, gargalos e recomendações técnicas.
            </p>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg mb-4 text-xs text-slate-600">
            <p className="font-medium mb-1">Abas do Relatório:</p>
            <div className="grid grid-cols-2 gap-1">
              <span>1. Visão Geral e Volume</span>
              <span>2. Cancelamentos e Absenteísmo</span>
              <span>3. Especialidades e Demandas</span>
              <span>4. Ranking de Desempenho</span>
              <span>5. Gargalos e Recomendações</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-xs">Data Inicial</Label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm mt-1"
                data-testid="operacional-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Data Final</Label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm mt-1"
                data-testid="operacional-end-date"
              />
            </div>
          </div>

          <Button 
            onClick={handleExportBalancoOperacional}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="export-balanco-operacional-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Balanço Operacional
          </Button>
        </div>
        )}

        {/* Documento de Aceites dos Termos - SOMENTE MASTER */}
        {isMaster && isMaster() && (
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-testid="terms-acceptances-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-amber-50">
              <ShieldCheck className="h-6 w-6 text-amber-600" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Aceite dos Termos (LGPD)</h2>
              <p className="text-sm text-slate-600">Documento exclusivo do Master</p>
            </div>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-amber-700">
              Documento de conformidade com a lista de todos os usuários que aceitaram os Termos de Uso
              e a Política de Privacidade, com nome, e-mail, perfil, unidade, data do aceite e versão.
            </p>
          </div>

          <Button
            onClick={handleExportTermsAcceptances}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="export-terms-acceptances-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Aceites dos Termos
          </Button>
        </div>
        )}

        {/* Informações */}
        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <Info className="h-6 w-6 text-slate-500" />
            <h3 className="text-lg font-semibold text-slate-900">Informações</h3>
          </div>
          <div className="space-y-4 text-sm text-slate-600">
            <p>
              <strong>Formatos:</strong> Os relatórios são exportados em formato Excel (.xlsx).
            </p>
            <p>
              <strong>Filtros:</strong> Use os filtros para refinar os dados exportados.
              Deixe em branco para exportar todos os registros.
            </p>
            <p>
              <strong>Colunas:</strong> Selecione as colunas que deseja incluir no relatório.
              Isso permite personalizar o conteúdo de acordo com sua necessidade.
            </p>
            <p>
              <strong>Datas:</strong> Use os filtros de data para exportar consultas de um período específico.
              Pode filtrar por data de atendimento ou data de solicitação.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
